import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ScanFace, Camera, RefreshCw, Check, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollFacePage() {
  const { token } = useParams();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [info, setInfo] = useState(null); // {name, company_name, already_enrolled}
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/face-enroll/${token}`);
        setInfo(data);
      } catch (e) {
        setPageError(e.response?.data?.detail || "This enrollment link is invalid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const startCamera = useCallback(async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch {
      setCameraError("Couldn't access the camera. Allow camera permission and reload, or open this link on a phone.");
    }
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!loading && info && !info.already_enrolled && !photo && !done) startCamera();
    return stopCamera;
  }, [loading, info, photo, done, startCamera, stopCamera]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    const w = 480;
    const h = Math.round((v.videoHeight / v.videoWidth) * w);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    setPhoto(c.toDataURL("image/jpeg", 0.8));
    stopCamera();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/face-enroll/${token}`, { photo_data_url: photo });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrollment failed — please try again.");
      setPhoto("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-50 flex items-center justify-center px-4 py-10" data-testid="enroll-face-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl p-7">
        <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-[0.25em] mb-4">
          <ShieldCheck className="h-4 w-4" /> Logerd · Face check setup
        </div>

        {loading && (
          <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        )}

        {!loading && pageError && (
          <div className="py-8 text-center" data-testid="enroll-face-error">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <div className="font-display text-lg font-semibold text-slate-900">Link problem</div>
            <p className="text-sm text-slate-600 mt-2">{pageError}</p>
          </div>
        )}

        {!loading && info && done && (
          <div className="py-8 text-center" data-testid="enroll-face-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
              <Check className="h-8 w-8" />
            </div>
            <div className="font-display text-xl font-semibold text-slate-900">You're all set, {info.name?.split(" ")[0]}!</div>
            <p className="text-sm text-slate-600 mt-2">
              From now on the kiosk camera will confirm it's really you when you clock in or out at {info.company_name}.
            </p>
          </div>
        )}

        {!loading && info && !done && (
          <>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">
              Hi {info.name?.split(" ")[0]}, take a quick selfie
            </h1>
            <p className="text-sm text-slate-600 mt-1.5 mb-5">
              {info.company_name} uses face verification for staff clock-in. Look straight at the camera in good light — no hats or sunglasses.
              {info.already_enrolled && <span className="block mt-1 text-amber-700 font-medium">You're already enrolled — submitting again will replace your current photo.</span>}
            </p>

            <div className="aspect-[4/3] w-full rounded-xl bg-slate-900 overflow-hidden relative mb-4">
              {photo ? (
                <img src={photo} alt="Your capture" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} data-testid="enroll-face-preview" />
              ) : (
                <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} data-testid="enroll-face-video" />
              )}
              {!photo && !cameraError && (
                <div className="absolute inset-x-12 top-6 bottom-6 border-2 border-white/40 rounded-[45%] pointer-events-none" />
              )}
            </div>

            {cameraError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2 mb-4" data-testid="enroll-face-camera-error">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {cameraError}
              </div>
            )}

            {!photo ? (
              <button
                onClick={capture}
                disabled={!!cameraError}
                data-testid="enroll-face-capture-btn"
                className="w-full h-12 rounded-xl bg-primary text-white font-semibold inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-50 transition"
              >
                <Camera className="h-5 w-5" /> Take photo
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPhoto("")}
                  disabled={submitting}
                  data-testid="enroll-face-retake-btn"
                  className="h-12 rounded-xl border border-slate-200 bg-white font-medium text-slate-700 inline-flex items-center justify-center gap-2 hover:bg-slate-50 transition"
                >
                  <RefreshCw className="h-4 w-4" /> Retake
                </button>
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="enroll-face-submit-btn"
                  className="h-12 rounded-xl bg-emerald-600 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-60 transition"
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanFace className="h-5 w-5" />} Use this photo
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
